import i18next from 'i18next';
import { IdName, SkillLevel } from './types';

export type SkillGroupBy = 'skill' | 'category' | 'branch';

export const allSkillGroupBy: SkillGroupBy[] = ['skill', 'category', 'branch'];

export const isSkillGroupBy = (value: unknown): value is SkillGroupBy =>
  typeof value === 'string' &&
  (allSkillGroupBy as string[]).includes(value as string);

export type GroupableSkill = {
  id: string;
  skillId?: string | null;
  name: string;
  category: IdName | null;
  path: string | null;
};

export type SkillGroup = {
  key: string;
  name: string;
  skillIds: string[];
};

const UNCATEGORIZED_KEY = '__uncategorized__';

// Path__c has the shape /rootId/childId/leafId/, so the first segment is the
// top-level branch a skill rolls up into. A skill with no path is its own root,
// keyed by its skill id — which is `skillId` when `id` is a different identity
// (e.g. the radar's employee-skill association id), otherwise `id`.
export const getSkillRootId = (skill: {
  id: string;
  skillId?: string | null;
  path: string | null;
}): string => {
  const segments = (skill.path ?? '').split('/').filter(Boolean);
  return segments[0] ?? skill.skillId ?? skill.id;
};

export const buildSkillGroups = (
  skills: GroupableSkill[],
  groupBy: Exclude<SkillGroupBy, 'skill'>,
  resolveName: (id: string) => string | undefined,
): SkillGroup[] => {
  const groups = new Map<string, SkillGroup>();
  for (const skill of skills) {
    const { key, name } =
      groupBy === 'category'
        ? {
            key: skill.category?.id ?? UNCATEGORIZED_KEY,
            name:
              skill.category?.name ??
              i18next.t('skills.grouping.uncategorized'),
          }
        : (() => {
            const rootId = getSkillRootId(skill);
            return { key: rootId, name: resolveName(rootId) ?? skill.name };
          })();

    const existing = groups.get(key);
    if (existing) {
      existing.skillIds.push(skill.id);
    } else {
      groups.set(key, { key, name, skillIds: [skill.id] });
    }
  }
  return [...groups.values()].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? ''),
  );
};

export type GroupContribution = { name: string; level: number };

export type GroupAverage = {
  average: number | null;
  contributions: GroupContribution[];
};

// Average of every rated skill in the group; unrated skills are excluded.
// An empty group (nothing rated) returns a null average.
export const computeGroupAverage = (
  skillIds: string[],
  getLevel: (skillId: string) => SkillLevel | undefined,
  getName: (skillId: string) => string,
): GroupAverage => {
  const contributions: GroupContribution[] = [];
  for (const id of skillIds) {
    const level = getLevel(id);
    if (level !== null && level !== undefined) {
      contributions.push({ name: getName(id), level });
    }
  }
  if (contributions.length === 0) {
    return { average: null, contributions };
  }
  const sum = contributions.reduce((acc, c) => acc + c.level, 0);
  return { average: sum / contributions.length, contributions };
};

export const formatAverage = (value: number): string => value.toFixed(1);

export const formatGroupAverageTooltip = (
  avg: GroupAverage,
): string | undefined => {
  if (avg.average === null || avg.contributions.length === 0) {
    return undefined;
  }
  const list = avg.contributions.map((c) => `${c.name} ${c.level}`).join(', ');
  // Rendered as a plain-text `title` attribute, so keep skill names verbatim
  // (default i18next escaping would turn & / < / > into visible entities).
  return i18next.t('skills.grouping.averageTooltip', {
    count: avg.contributions.length,
    list,
    average: formatAverage(avg.average),
    interpolation: { escapeValue: false },
  });
};
